import type { ReactNode } from 'react';
import type { MouseEvent as ReactMouseEvent, PointerEvent as ReactPointerEvent } from 'react';
import { usePressFeedbackClick } from '../../hooks/usePressFeedbackClick.js';
import { ProximityScope } from '../../primitives/ProximityHighlight.js';
import { cx } from '../../utils/cx.js';
import { isDataTableRowPointerBlocked } from '../useDataTableRowHorizontalScroll.js';
import { DataTableEmptyState } from './DataTableEmptyState.js';
import { DataTableRowActionsContent } from './DataTableRowActions.js';
import { DataTableRowSelectControl } from './DataTableRowSelectControl.js';
import type { Column, DataTableSelection } from './types.js';
import styles from './DataTable.module.css';

function StackedCard<T>({
  row,
  columns,
  rowKey,
  selection,
  selectionActive: _selectionActive,
  onToggleRowSelection,
  hasActionColumn,
  rowLeadingActions,
  rowActions,
  onRowClick,
  isRowClickable,
  onRowPointerEnter,
  rowProps,
}: {
  row: T;
  columns: Column<T>[];
  rowKey: (row: T) => string;
  selection?: DataTableSelection<T>;
  selectionActive?: boolean;
  onToggleRowSelection?: (key: string) => void;
  hasActionColumn: boolean;
  rowLeadingActions?: (row: T) => ReactNode;
  rowActions?: (row: T) => ReactNode;
  onRowClick?: (row: T) => void;
  isRowClickable?: (row: T) => boolean;
  onRowPointerEnter?: (row: T) => void;
  rowProps?: (row: T) => import('react').HTMLAttributes<HTMLTableRowElement> | undefined;
}) {
  const extra = rowProps?.(row);
  const {
    className: extraRowClass,
    onClick: extraRowOnClick,
    onPointerEnter: extraRowPointerEnter,
    ...extraRowRest
  } = extra ?? {};
  const rowClickable =
    Boolean(extra?.onClick) ||
    (Boolean(onRowClick) && (isRowClickable ? isRowClickable(row) : true));
  const rowClick = rowClickable ? onRowClick : undefined;
  const mergedStackedOnClick =
    rowClick || extraRowOnClick
      ? (e: ReactMouseEvent<HTMLDivElement>) => {
          extraRowOnClick?.(e as ReactMouseEvent<HTMLTableRowElement>);
          if (!rowClick) return;
          if (e.defaultPrevented) return;
          if (isDataTableRowPointerBlocked(e.target)) return;
          rowClick(row);
        }
      : undefined;
  const { onClick: pressOnClick, pressAttrs } = usePressFeedbackClick(mergedStackedOnClick, {
    disabled: !mergedStackedOnClick,
  });
  const mergedStackedPointerEnter =
    onRowPointerEnter || extraRowPointerEnter
      ? (e: ReactPointerEvent<HTMLDivElement>) => {
          extraRowPointerEnter?.(e as ReactPointerEvent<HTMLTableRowElement>);
          onRowPointerEnter?.(row);
        }
      : undefined;

  return (
    <div
      data-wp-datatable-card
      {...(rowClickable ? { 'data-wp-proximity-item': '' } : {})}
      {...extraRowRest}
      className={cx(styles.stackedCard, rowClickable && styles.stackedCardClickable, extraRowClass)}
      role={mergedStackedOnClick ? 'button' : undefined}
      tabIndex={mergedStackedOnClick ? 0 : undefined}
      onClick={mergedStackedOnClick ? pressOnClick : undefined}
      {...(mergedStackedOnClick ? pressAttrs : {})}
      onPointerEnter={mergedStackedPointerEnter}
      onKeyDown={
        mergedStackedOnClick
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                if (isDataTableRowPointerBlocked(e.target)) return;
                rowClick?.(row);
              }
            }
          : undefined
      }
    >
      {selection ? (
        <DataTableRowSelectControl
          checked={selection.selectedKeys.has(rowKey(row))}
          disabled={selection.isRowSelectable?.(row) === false}
          ariaLabel={`Sélectionner la ligne ${rowKey(row)}`}
          onChange={() => onToggleRowSelection?.(rowKey(row))}
        />
      ) : null}
      {columns.map((c) => (
        <div key={c.key} className={styles.stackedField}>
          <span className={styles.stackedLabel}>
            {typeof c.label === 'string' && c.label.trim() ? c.label : (c.headerAriaLabel ?? c.key)}
          </span>
          <div className={styles.stackedValue}>
            {c.render ? c.render(row) : String((row as Record<string, unknown>)[c.key] ?? '')}
          </div>
        </div>
      ))}
      {hasActionColumn ? (
        <DataTableRowActionsContent
          stacked
          leading={rowLeadingActions?.(row)}
          menu={rowActions?.(row)}
        />
      ) : null}
    </div>
  );
}

export function DataTableStackedView<T>({
  columns,
  rowSource,
  dataLength,
  emptyMessage,
  emptyAction,
  emptyVariant = 'default',
  rowKey,
  hasActionColumn,
  selection,
  selectionActive = false,
  onToggleRowSelection,
  rowLeadingActions,
  rowActions,
  onRowClick,
  isRowClickable,
  onRowPointerEnter,
  proximityHover = false,
  rowProps,
}: {
  columns: Column<T>[];
  rowSource: T[];
  dataLength: number;
  emptyMessage: string;
  emptyAction?: ReactNode;
  emptyVariant?: 'default' | 'filter' | 'resource';
  rowKey: (row: T) => string;
  hasActionColumn: boolean;
  selection?: DataTableSelection<T>;
  selectionActive?: boolean;
  onToggleRowSelection?: (key: string) => void;
  rowLeadingActions?: (row: T) => ReactNode;
  rowActions?: (row: T) => ReactNode;
  onRowClick?: (row: T) => void;
  isRowClickable?: (row: T) => boolean;
  onRowPointerEnter?: (row: T) => void;
  proximityHover?: boolean;
  rowProps?: (row: T) => import('react').HTMLAttributes<HTMLTableRowElement> | undefined;
}) {
  const cards =
    dataLength === 0 ? (
      <DataTableEmptyState
        emptyMessage={emptyMessage}
        emptyAction={emptyAction}
        emptyVariant={emptyVariant}
        overlay={false}
      />
    ) : (
      rowSource.map((row) => (
        <StackedCard
          key={rowKey(row)}
          row={row}
          columns={columns}
          rowKey={rowKey}
          selection={selection}
          selectionActive={selectionActive}
          onToggleRowSelection={onToggleRowSelection}
          hasActionColumn={hasActionColumn}
          rowLeadingActions={rowLeadingActions}
          rowActions={rowActions}
          onRowClick={onRowClick}
          isRowClickable={isRowClickable}
          onRowPointerEnter={onRowPointerEnter}
          rowProps={rowProps}
        />
      ))
    );

  const list = proximityHover ? (
    <ProximityScope axis="y" className={styles.stackedList}>
      {cards}
    </ProximityScope>
  ) : (
    <div className={styles.stackedList}>{cards}</div>
  );

  return (
    <div
      {...(selection && selectionActive ? { 'data-wp-datatable-selection-active': '' } : {})}
      {...(proximityHover ? { 'data-wp-proximity': 'on' } : {})}
    >
      {list}
    </div>
  );
}
